var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import CloseIcon from '@mui/icons-material/Close';
import { RedesignAuthForm, AuthFormModeSwitch } from 'root/src/client/v2/auth/features/AuthForm/RedesignAuthForm';
import { DareDropLogo } from './signInIcons';
// In-context auth dialog: the shared RedesignAuthForm in a branded modal. It swaps panels in place
// and closes on success instead of navigating, which is what the standalone /sign-in and /sign-up
// pages do with the very same form.
export const SignInDialog = ({ children, open, onOpenChange, onSignIn, initialMode = 'signin' }) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const [mode, setMode] = useState(initialMode);
    const isControlled = open !== undefined;
    const dialogOpen = isControlled ? open : internalOpen;
    const setDialogOpen = (value) => {
        if (isControlled) {
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(value);
        }
        else {
            setInternalOpen(value);
        }
    };
    // Always defined, so the form's sign-in never falls back to the `login` thunk's own navigation:
    // the dialog owns what happens after a successful sign-in.
    const handleSignIn = () => __awaiter(void 0, void 0, void 0, function* () {
        yield (onSignIn === null || onSignIn === void 0 ? void 0 : onSignIn());
    });
    return (<DialogPrimitive.Root open={dialogOpen} onOpenChange={setDialogOpen}>
			{children && <DialogPrimitive.Trigger asChild>{children}</DialogPrimitive.Trigger>}
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-[1100] bg-black/80 data-[state=open]:animate-dialog-overlay-show data-[state=closed]:animate-dialog-overlay-hide"/>
				<DialogPrimitive.Content className="redesign fixed left-1/2 top-1/2 z-[1101] w-full -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-white/[0.06] bg-card text-foreground shadow-2xl shadow-black/40 sm:max-w-[600px] data-[state=open]:animate-dialog-content-show data-[state=closed]:animate-dialog-content-hide">
					<DialogPrimitive.Title className="sr-only">Sign in</DialogPrimitive.Title>
					<DialogPrimitive.Description className="sr-only">Sign in to your Dare Drop account.</DialogPrimitive.Description>

					<div className="grid min-h-[480px] grid-cols-1 sm:grid-cols-[240px_1fr]">
						{/* Left branding panel */}
						<div className="relative hidden flex-col justify-between overflow-hidden rounded-l-2xl p-6 sm:flex" style={{ background: 'linear-gradient(160deg, var(--grad-brand-from) 0%, hsl(262 83% 40%) 40%, hsl(200 80% 35%) 70%, var(--grad-accent-from) 100%)' }}>
							<div className="absolute right-4 top-8 h-16 w-16 rounded-full border border-white/[0.15] opacity-40"/>
							<div className="absolute bottom-20 left-3 h-10 w-10 rotate-12 rounded-lg border border-white/[0.12] opacity-30"/>
							<div className="absolute right-8 top-1/2 h-6 w-6 rounded-full bg-white/[0.08]"/>
							<div className="absolute bottom-32 right-6 h-px w-20 rotate-45 bg-white/[0.15]"/>

							<div>
								<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.15] backdrop-blur-sm">
									<DareDropLogo className="h-5 w-5 text-white"/>
								</div>
								<h3 className="mt-5 font-display text-lg font-bold leading-tight text-white">Dare Drop</h3>
								<p className="mt-2 text-[13px] leading-relaxed text-white/60">
									Discover game drops, support creators, connect with publishers.
								</p>
							</div>

							<p className="text-[10px] uppercase tracking-wider text-white/30">Dare to discover</p>
						</div>

						{/* Right form panel */}
						<div className="flex flex-col">
							<div className="flex flex-1 flex-col justify-center p-6 sm:p-7">
								<RedesignAuthForm mode={mode} onModeChange={setMode} onSignIn={handleSignIn} onSignInSuccess={() => setDialogOpen(false)} onProviderSignIn={() => setDialogOpen(false)}/>
							</div>

							<div className="border-t border-white/[0.04] px-6 py-3">
								<AuthFormModeSwitch mode={mode} onModeChange={setMode}/>
							</div>
						</div>
					</div>

					<DialogPrimitive.Close className="absolute right-4 top-4 z-30 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-muted-foreground focus:ring-offset-2">
						<CloseIcon sx={{ fontSize: 18 }}/>
						<span className="sr-only">Close</span>
					</DialogPrimitive.Close>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>);
};
